import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Footer from "../../CoreComponents/Footer";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import { loadInsights } from "./Functions";
import FilterBar from "./FilterBar";
import KpiTiles from "./blocks/KpiTiles";
import TrendHero from "./blocks/TrendHero";
import HourHeatmap from "./blocks/HourHeatmap";
import ChannelBreakdown from "./blocks/ChannelBreakdown";
import TopListBlock from "./blocks/TopListBlock";
import { count, fmtDate, minutes, pct, qty, rupeesWhole } from "./format";
import "./insights.css";
import { picked_franchise_id } from "../../Functionalities/Config";

const FIRST_BLOCKS = "tiles,daily,breakdown";
const LIST_BLOCKS =
  "top_restaurants,top_items,cancelled_items,cancelled_stores,declined_stores,ratings,customers,delivery_time,hours,promos,zero_sellers";

const INITIAL = { from: "", to: "", franchise: picked_franchise_id, branch: "", fulfilment: "", payment_mode: "", category: "", sort: "orders", amount: "food" };

const RESTAURANT_COLUMNS = [
  { key: "name", label: "Store Name" },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true },
  { key: "net_amount", label: "Revenue", numeric: true, format: rupeesWhole },
  { key: "cancellation_rate", label: "Cancel %", numeric: true, format: pct, center: true },
  { key: "avg_wait_minutes", label: "Avg Wait", numeric: true, format: minutes, center: true },
];

const ITEM_COLUMNS = [
  {
    key: "name",
    label: "Item & Outlet",
    sub: (row) => (row.branch ? `${row.branch}${row.category ? ` • ${row.category}` : ""}` : row.category),
  },
  { key: "qty", label: "Qty", numeric: true, format: qty, center: true },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true },
  { key: "net_amount", label: "Revenue", numeric: true, format: rupeesWhole },
];

const CUSTOMER_COLUMNS = [
  { key: "name", label: "Customer", sub: (row) => row.mobile },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true, bold: true },
  { key: "net_amount", label: "Total Spent", numeric: true, format: rupeesWhole },
  { key: "avg_order_value", label: "Avg Order", numeric: true, format: rupeesWhole },
  { key: "last_order", label: "Last Order", format: fmtDate, numeric: true },
];

const ZERO_COLUMNS = [
  { key: "name", label: "Menu Item", sub: (row) => row.category },
  { key: "branch", label: "Store Location" },
];

const CANCELLED_ITEM_COLUMNS = [
  { key: "name", label: "Item & Outlet", sub: (row) => row.branch },
  { key: "qty_cancelled", label: "In cancelled orders", numeric: true, format: qty, center: true },
  { key: "orders_cancelled", label: "Orders", numeric: true, format: count, center: true },
  { key: "qty", label: "Sold", numeric: true, format: qty, center: true },
];

const CANCELLED_STORE_COLUMNS = [
  { key: "name", label: "Store" },
  { key: "rate", label: "Cancel %", numeric: true, format: pct, center: true },
  { key: "cancelled", label: "Cancelled", numeric: true, format: count, center: true },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true },
];

const DECLINED_STORE_COLUMNS = [
  { key: "name", label: "Store" },
  { key: "rate", label: "Reject %", numeric: true, format: pct, center: true },
  { key: "declined", label: "Declined", numeric: true, format: count, center: true },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true },
];

const PROMO_COLUMNS = [
  { key: "code", label: "Promo Code", bold: true },
  { key: "orders", label: "Orders", numeric: true, format: count, center: true },
  { key: "discount_given", label: "Discount Given", numeric: true, format: rupeesWhole },
  { key: "net_amount", label: "Revenue Generated", numeric: true, format: rupeesWhole },
  {
    key: "revenue_per_discount_rupee",
    label: "Revenue / ₹1 disc",
    numeric: true,
    format: (val) => (val == null ? "--" : Number(val).toFixed(2)),
  },
];

const RATING_COLUMNS = [
  { key: "name", label: "Name", sub: (row) => row.branch },
  { key: "avg", label: "Rating", numeric: true, format: (val) => `${Number(val || 0).toFixed(1)} ★`, center: true },
  { key: "votes", label: "Votes", numeric: true, format: count, center: true },
];

export default function Insights() {
  const history = useHistory();
  const [filters, setFilters] = useState(INITIAL);
  const [first, setFirst] = useState(null);
  const [lists, setLists] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("cancelled_items");
  const [showQualitySection, setShowQualitySection] = useState(true);
  const [loading, setLoading] = useState(false);

  const fetchInsights = () => {
    setLoading(true);
    setFirst(null);
    setLists(null);
    setExpanded({});
    setError("");

    let doneCount = 0;
    const finish = () => {
      doneCount += 1;
      if (doneCount >= 2) setLoading(false);
    };

    loadInsights({ ...filters, blocks: FIRST_BLOCKS })
      .then((data) => {
        if (!data) setError("Insights could not be loaded. Try again in a moment.");
        else setFirst(data);
      })
      .catch(() => setError("Insights could not be loaded."))
      .finally(finish);

    loadInsights({ ...filters, blocks: LIST_BLOCKS, limit: 10 })
      .then((data) => {
        if (data) setLists(data);
      })
      .catch(() => {})
      .finally(finish);
  };

  useEffect(() => {
    fetchInsights();
  }, [filters]);

  const showAll = (block) =>
    loadInsights({ ...filters, blocks: block, limit: 100 }).then((data) => {
      if (data && data[block]) setExpanded((prev) => ({ ...prev, [block]: data[block] }));
    });

  const rowsFor = (block) => expanded[block] || (lists ? lists[block] : undefined);
  const dataThrough = first && first.data_through;
  const notes = (first && first.notes) || [];
  const breakdown = first && first.breakdown;

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Insights"} />
      <NavBar />

      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid insights" style={{ maxWidth: "1680px", margin: "0 auto", padding: "1.25rem" }}>
          <FilterBar
            filters={filters}
            range={first && first.range}
            dataFrom={first && first.data_from}
            dataThrough={dataThrough}
            onChange={setFilters}
            onRefresh={fetchInsights}
            loading={loading}
          />

          {error && <div className="alert alert-danger mb-3">{error}</div>}
          {first && first.data_through === null && (
            <div className="alert alert-warning mb-3">
              Nothing has been built yet. The nightly job fills this in after the first closed day.
            </div>
          )}
          {notes.length > 0 && (
            <div className="alert alert-info mb-3" style={{ fontSize: "0.8125rem" }}>
              {notes.join(". ")}.
            </div>
          )}

          <KpiTiles tiles={first && first.tiles} daily={first && first.daily} amount={filters.amount} />

          <div className="ins-row-trend-heat ins-section">
            <TrendHero daily={first && first.daily} range={first && first.range} compareRange={first && first.compare_range} amount={filters.amount} />
            <HourHeatmap data={rowsFor("hours")} />
          </div>

          <ChannelBreakdown breakdown={breakdown} deliveryData={rowsFor("delivery_time")} amount={filters.amount} />

          <div className="ins-row-split-7-5 ins-section">
            <TopListBlock
              title="Most ordered items"
              subtitle="Volume, order frequency and revenue performance"
              rows={rowsFor("top_items")}
              columns={ITEM_COLUMNS}
              onShowAll={() => showAll("top_items")}
              exportName="insights-top-items"
              showQtyBar={true}
            />

            <TopListBlock
              title="Top performing stores"
              subtitle="Ranked by revenue and cancellation metrics"
              rows={rowsFor("top_restaurants")}
              columns={RESTAURANT_COLUMNS}
              onShowAll={() => showAll("top_restaurants")}
              exportName="insights-top-stores"
              isStoreTable={true}
            />
          </div>

          <div className="ins-row-split-6-6 ins-section">
            <TopListBlock
              title="Most ordering customers"
              subtitle="High-frequency patrons with gross spend breakdown"
              rows={rowsFor("customers")}
              columns={CUSTOMER_COLUMNS}
              onShowAll={() => showAll("customers")}
              exportName="insights-customers"
            />

            <TopListBlock
              title="Zero-sellers"
              subtitle="Menu items with 0 sales in the selected date range"
              badgeText="Needs attention"
              badgeType="rose"
              rows={rowsFor("zero_sellers")}
              columns={ZERO_COLUMNS}
              onShowAll={() => showAll("zero_sellers")}
              exportName="insights-zero-sellers"
              actionButtonLabel="Create Promo"
              onActionClick={() => history.push("/promocode")}
            />
          </div>

          <div className="ins-section">
            <div
              className="ins-extra-section-header"
              onClick={() => setShowQualitySection(!showQualitySection)}
              title="Click to expand/collapse audit section"
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <span className="material-symbols-outlined" style={{ color: "#4648d4" }}>verified</span>
                <div>
                  <h4 style={{ margin: 0, fontSize: "0.9375rem", fontWeight: 700, color: "#0f172a" }}>
                    Quality, Rejections &amp; Promotions Audit
                  </h4>
                  <p style={{ margin: 0, fontSize: "0.75rem", color: "#64748b" }}>
                    Detailed analysis of cancellations, rejections, ratings and promotional impact
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span className="material-symbols-outlined" style={{ color: "#64748b", transform: showQualitySection ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}>
                  expand_more
                </span>
              </div>
            </div>

            {showQualitySection && (
              <div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
                  {[
                    { id: "cancelled_items", label: "Items in Cancelled Orders" },
                    { id: "cancelled_stores", label: "Most Cancelled Stores" },
                    { id: "declined_stores", label: "Stores Rejecting Orders" },
                    { id: "ratings", label: "Highest Rated" },
                    { id: "promos", label: "Promo Codes Performance" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`ins-tab-pill ${activeTab === tab.id ? "active" : ""}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === "cancelled_items" && (
                  <TopListBlock
                    title="Items in cancelled orders"
                    note="Items that sat inside cancelled orders — there is no per-item cancellation record."
                    rows={rowsFor("cancelled_items")}
                    columns={CANCELLED_ITEM_COLUMNS}
                    onShowAll={() => showAll("cancelled_items")}
                    exportName="insights-cancelled-items"
                    showQtyBar={true}
                  />
                )}

                {activeTab === "cancelled_stores" && (
                  <TopListBlock
                    title="Most cancelled stores"
                    note="Stores with fewer than 10 orders in the range are excluded."
                    rows={rowsFor("cancelled_stores")}
                    columns={CANCELLED_STORE_COLUMNS}
                    exportName="insights-cancelled-stores"
                    isStoreTable={true}
                  />
                )}

                {activeTab === "declined_stores" && (
                  <TopListBlock
                    title="Stores rejecting orders"
                    note="Orders the restaurant declined after the customer placed them."
                    rows={rowsFor("declined_stores")}
                    columns={DECLINED_STORE_COLUMNS}
                    exportName="insights-declined-stores"
                    isStoreTable={true}
                  />
                )}

                {activeTab === "ratings" && (
                  <div className="ins-row-split-6-6">
                    <TopListBlock
                      title="Highest rated stores"
                      note="All time rating averages. Stores with fewer than minimum votes excluded."
                      rows={lists && lists.ratings ? lists.ratings.stores : undefined}
                      columns={RATING_COLUMNS}
                      exportName="insights-rated-stores"
                      isStoreTable={true}
                    />
                    <TopListBlock
                      title="Highest rated items"
                      note="Customer rating averages across menu items."
                      rows={lists && lists.ratings ? lists.ratings.items : undefined}
                      columns={RATING_COLUMNS}
                      exportName="insights-rated-items"
                    />
                  </div>
                )}

                {activeTab === "promos" && (
                  <TopListBlock
                    title="Promo codes performance"
                    note="Orders, gross discount given and realized net revenue per promo campaign."
                    rows={rowsFor("promos")}
                    columns={PROMO_COLUMNS}
                    exportName="insights-promos"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
